import type { ColumnType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { useState } from "react";
import type { TableColumnGroupDefinition } from "~types/tableColumnGroupDefinition.ts";
import { getTableColumns } from "~utilities/getTableColumns.ts";

type ColumnSettingsConfig = {
  columnSettingsLocalStorageKey: string;
  tableConfiguration: Array<TableColumnGroupDefinition>;
};

type ColumnSortConfig<T> = {
  sorterLocalStorageKey: string;
  defaultSorter?: SorterResult<T>;
};

type TableColumnsConfig<T> = {
  columnSettingsConfig: ColumnSettingsConfig;
  columnSortConfig: ColumnSortConfig<T>;
  tableColumns: ColumnType<T>[];
};

export const useTableColumns = <T>(config: TableColumnsConfig<T>) => {
  const initialSelectedColumns = getTableColumns(
    config.columnSettingsConfig.columnSettingsLocalStorageKey,
    config.columnSettingsConfig.tableConfiguration,
  );

  const [selectedColumns, setSelectedColumns] = useState(initialSelectedColumns);
  const [sortConfig, setSortConfig] = useState<SorterResult<T> | SorterResult<T>[]>(() => {
    const saved = localStorage.getItem(config.columnSortConfig.sorterLocalStorageKey);
    if (saved) {
      return JSON.parse(saved);
    }
    return config.columnSortConfig.defaultSorter || {};
  });

  const setSortColumn = (sorter: SorterResult<T> | SorterResult<T>[]) => {
    setSortConfig(sorter);
    localStorage.setItem(config.columnSortConfig.sorterLocalStorageKey, JSON.stringify(sorter));
  };

  const columnsToShow = config.tableColumns
    .map((column) => ({
      ...column,
      ...(Array.isArray(sortConfig)
        ? sortConfig.find((sort) => sort.columnKey === column.key)?.order && {
            defaultSortOrder: sortConfig.find((sort) => sort.columnKey === column.key)?.order,
          }
        : sortConfig?.columnKey === column.key && {
            defaultSortOrder: sortConfig?.order,
          }),
    }))
    .filter((x) => !x.key || selectedColumns.includes(JSON.stringify(x.key)));

  return { columnsToShow, setSelectedColumns, setSortColumn };
};
